import { useState, useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { X, Tag, Settings } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

interface SavedTag {
  id: string;
  tag_name: string;
  color: string;
  usage_count: number;
}

interface TagAutocompleteProps {
  selectedTags: string[];
  onTagsChange: (tags: string[]) => void;
  unaId?: string;
  onManageClick: () => void;
}

export const TagAutocomplete = ({ 
  selectedTags, 
  onTagsChange, 
  unaId,
  onManageClick 
}: TagAutocompleteProps) => {
  const { user } = useAuth();
  const [inputValue, setInputValue] = useState("");
  const [savedTags, setSavedTags] = useState<SavedTag[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [filteredTags, setFilteredTags] = useState<SavedTag[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (user) {
      fetchSavedTags();
    }
  }, [user, unaId]);

  useEffect(() => {
    // Filter tags based on input
    const filtered = savedTags
      .filter(tag => 
        tag.tag_name.toLowerCase().includes(inputValue.toLowerCase()) &&
        !selectedTags.includes(tag.tag_name)
      )
      .sort((a, b) => b.usage_count - a.usage_count)
      .slice(0, 10);
    
    setFilteredTags(filtered);
  }, [inputValue, savedTags, selectedTags]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        !inputRef.current?.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const fetchSavedTags = async () => {
    try {
      const query = supabase
        .from("saved_tags")
        .select("*")
        .eq("user_id", user!.id)
        .order("usage_count", { ascending: false });

      if (unaId) {
        query.eq("una_id", unaId);
      }

      const { data, error } = await query;
      if (error) throw error;
      setSavedTags(data || []);
    } catch (error) {
      console.error("Error fetching tags:", error);
    }
  };

  const saveNewTag = async (tagName: string) => {
    try {
      const { error } = await supabase
        .from("saved_tags")
        .insert({
          user_id: user!.id,
          una_id: unaId || null,
          tag_name: tagName,
          usage_count: 1
        });

      if (error && error.code !== "23505") { // Ignore duplicate errors
        throw error;
      }

      // Increment usage count if tag already exists
      if (error?.code === "23505") {
        const { data: existing } = await supabase
          .from("saved_tags")
          .select("usage_count")
          .eq("user_id", user!.id)
          .eq("tag_name", tagName)
          .single();
        
        if (existing) {
          await supabase
            .from("saved_tags")
            .update({ usage_count: existing.usage_count + 1 })
            .eq("user_id", user!.id)
            .eq("tag_name", tagName);
        }
      }

      fetchSavedTags();
    } catch (error) {
      console.error("Error saving tag:", error);
    }
  };

  const handleSelectTag = async (tagName: string) => {
    if (!selectedTags.includes(tagName)) {
      onTagsChange([...selectedTags, tagName]);
      
      // Increment usage count
      const { data: existing } = await supabase
        .from("saved_tags")
        .select("usage_count")
        .eq("user_id", user!.id)
        .eq("tag_name", tagName)
        .single();
      
      if (existing) {
        await supabase
          .from("saved_tags")
          .update({ usage_count: existing.usage_count + 1 })
          .eq("user_id", user!.id)
          .eq("tag_name", tagName);
      }
      
      setInputValue("");
      setShowDropdown(false);
    }
  };

  const handleAddNewTag = async () => {
    const trimmedTag = inputValue.trim();
    if (!trimmedTag) return;

    if (selectedTags.includes(trimmedTag)) {
      toast.error("Tag already added");
      return;
    }

    if (trimmedTag.length > 50) {
      toast.error("Tag name must be less than 50 characters");
      return;
    }

    onTagsChange([...selectedTags, trimmedTag]);
    await saveNewTag(trimmedTag);
    setInputValue("");
    setShowDropdown(false);
  };

  const handleRemoveTag = (tagToRemove: string) => {
    onTagsChange(selectedTags.filter(tag => tag !== tagToRemove));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (filteredTags.length > 0) {
        handleSelectTag(filteredTags[0].tag_name);
      } else if (inputValue.trim()) {
        handleAddNewTag();
      }
    } else if (e.key === "Escape") {
      setShowDropdown(false);
    }
  };

  const getTagColor = (color: string) => {
    const colors: Record<string, string> = {
      red: "bg-red-100 text-red-700 border-red-300",
      blue: "bg-blue-100 text-blue-700 border-blue-300",
      green: "bg-green-100 text-green-700 border-green-300",
      yellow: "bg-yellow-100 text-yellow-700 border-yellow-300",
      purple: "bg-purple-100 text-purple-700 border-purple-300",
      orange: "bg-orange-100 text-orange-700 border-orange-300",
      gray: "bg-gray-100 text-gray-700 border-gray-300",
    };
    return colors[color] || colors.blue;
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium">🏷️ Tags (Optional)</label>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onManageClick}
          className="h-8 text-xs"
        >
          <Settings className="h-3 w-3 mr-1" />
          Manage Tags
        </Button>
      </div>

      <div className="relative">
        <Input
          ref={inputRef}
          type="text"
          placeholder="Type to search or add new tag..."
          value={inputValue}
          onChange={(e) => {
            setInputValue(e.target.value);
            setShowDropdown(true);
          }}
          onFocus={() => setShowDropdown(true)}
          onKeyDown={handleKeyDown}
          className="pr-10"
        />
        <Tag className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />

        {showDropdown && (inputValue || filteredTags.length > 0) && (
          <div
            ref={dropdownRef}
            className="absolute z-50 w-full mt-1 bg-background border rounded-md shadow-lg max-h-[300px] overflow-auto"
          >
            {filteredTags.length > 0 && (
              <div className="p-2">
                <div className="text-xs font-medium text-muted-foreground px-2 py-1">
                  Saved Tags:
                </div>
                {filteredTags.map((tag) => (
                  <button
                    key={tag.id}
                    type="button"
                    onClick={() => handleSelectTag(tag.tag_name)}
                    className="w-full flex items-center justify-between px-3 py-2 hover:bg-accent rounded-md text-sm"
                  >
                    <span className="flex items-center gap-2">
                      <span className={cn("w-3 h-3 rounded-full", getTagColor(tag.color))} />
                      #{tag.tag_name}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      used {tag.usage_count}×
                    </span>
                  </button>
                ))}
              </div>
            )}

            {inputValue.trim() && !savedTags.find(t => t.tag_name === inputValue.trim()) && (
              <>
                {filteredTags.length > 0 && (
                  <div className="border-t border-border" />
                )}
                <button
                  type="button"
                  onClick={handleAddNewTag}
                  className="w-full flex items-center gap-2 px-3 py-2 hover:bg-accent text-sm text-primary"
                >
                  <span>+ Add "{inputValue.trim()}" as new tag</span>
                </button>
              </>
            )}
          </div>
        )}
      </div>

      {selectedTags.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {selectedTags.map((tag) => {
            const savedTag = savedTags.find(t => t.tag_name === tag);
            return (
              <Badge
                key={tag}
                variant="secondary"
                className={cn(
                  "gap-1 pl-3 pr-1",
                  savedTag && getTagColor(savedTag.color)
                )}
              >
                #{tag}
                <button
                  type="button"
                  onClick={() => handleRemoveTag(tag)}
                  className="ml-1 hover:bg-background/50 rounded-full p-0.5"
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            );
          })}
        </div>
      )}
    </div>
  );
};
